import React, { useContext, useEffect, useState } from "react";
import SimpleModal from "../dialogs/SimpleModal";
import {
  Box,
  Button,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import useNotificationStore from "common/store/NotificationStore";
import theme from "common/theme/theme";
import { LanguageContext } from "common/contexts/LanguageContext";
import { SharedProjectContext } from "components/project/Project";
import CustomTablePagination from "components/project/components/list/CustomTablePagination";

interface IProps {
  isOpen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const NotificationListDialog: React.FC<IProps> = ({ isOpen, setOpen }) => {
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const { persistedNotifications, clearPersistedNotifications, currentBatchID } = useNotificationStore();

  const { getString } = useContext(LanguageContext);
  const { setIsBusyBackgroundActive, networkHealth, setTriggerCalculations } = useContext(SharedProjectContext);

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - persistedNotifications.length) : 0;

  const visibleRows = React.useMemo(
    () => persistedNotifications.slice().slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [page, rowsPerPage, persistedNotifications],
  );

  useEffect(() => {
    setIsBusyBackgroundActive(isOpen);
  }, [isOpen, setIsBusyBackgroundActive])

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log(`rowsPerPage: ${parseInt(event.target.value, 10)}`);
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <SimpleModal title={getString("dlg_msgList_title")} maxWidth="md" isOpen={isOpen} setOpen={setOpen}>
      <Grid container spacing={2} sx={{marginTop: "0px" }}>
        <Grid item xs={4}>
        <Box display="flex" justifyContent="flex-end">
          <Button variant="contained" onClick={() => setTriggerCalculations(true)}>
            {getString("dlg_msgList_run_calculations")}
          </Button>
        </Box>
        </Grid>
        <Grid item xs={4} sx={{alignContent: "center"}}>
        <Typography sx={{justifyItems: "center"}}>{networkHealth ? getString("dlg_msgList_compliant") : getString("dlg_msgList_errors")}</Typography>
        </Grid>
        <Grid item xs={4}>
        <Box display="flex" justifyContent="flex-end">
          <Button variant="contained" onClick={clearPersistedNotifications}>
            {getString("dlg_msgList_clear")}
          </Button>
        </Box>
        </Grid>
      </Grid>
      <Paper sx={{ width: "100%", mb: 2, mt: 2 }}>
        <TableContainer>
          <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size="medium">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    align: "left",
                    padding: "normal",
                    width: "auto",
                    fontWeight: "bold",
                    bgcolor: theme.palette.primary.light,
                  }}
                >
                  {getString("dlg_msgList_hdr_time")}
                </TableCell>
                <TableCell
                  sx={{
                    align: "left",
                    padding: "normal",
                    width: "auto",
                    fontWeight: "bold",
                    bgcolor: theme.palette.primary.light,
                  }}
                >
                  {getString("dlg_msgList_hdr_severity")}
                </TableCell>
                <TableCell
                  sx={{
                    align: "left",
                    padding: "normal",
                    minWidth: 0,
                    flex: 1,
                    fontWeight: "bold",
                    bgcolor: theme.palette.primary.light,
                  }}
                >
                  {getString("dlg_msgList_hdr_msg")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((row, index) => {
                const date: Date = row.date === undefined ? new Date() : (typeof row.date === 'string') ? new Date(row.date) : row.date; 
                return (
                  <TableRow
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={index}
                    sx={{
                      cursor: "selector",
                      "&.MuiTableRow-root:hover": {
                        backgroundColor: theme.accent.hover,
                        cursor: "selector",
                      },
                      backgroundColor: row.batchID === (currentBatchID - 1) ? "white" : theme.secret.gray,
                    }}
                  >
                    <TableCell align="left" sx={{ display: 'flex', flexDirection: 'column' }}>
                      <div>{date.toLocaleDateString()}</div>
                      <div>{date.toLocaleTimeString(navigator.language, { hour: '2-digit', minute: '2-digit' })}</div>
                    </TableCell>
                    <TableCell align="left">{row.severity}</TableCell>
                    <TableCell align="left">{row.message}</TableCell>
                  </TableRow>
                );
              })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: 53 * emptyRows,
                  }}
                >
                  <TableCell colSpan={2} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <CustomTablePagination
          rowsPerPageOptions={[5, 10, 25]}
          count={persistedNotifications.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </SimpleModal>
  );
};

export default NotificationListDialog;
